import { FormItem, Select } from '@lmc/ui-kit'
import { Text } from '@lmc/ui-kit/src/general'
import { Form as AntdForm, InputNumber, Switch } from 'antd'
import { useGetInvoicingRateCards } from 'screens/Rostering/hooks/useInvoicingRates'
import {
  type EventInvoicingRateOverride,
  useVisitRateCard
} from 'screens/Rostering/hooks/useVisitRateCard'
import { resolveEffectiveRate } from 'screens/Rostering/utils'
import styled from 'styled-components'

const CUSTOM_VALUE = '__custom__'

interface InvoicingRateSectionProps {
  residentId?: string
  atDate?: string
  savedOverride?: EventInvoicingRateOverride | null
}

export const InvoicingRateSection = ({
  residentId,
  atDate,
  savedOverride
}: InvoicingRateSectionProps) => {
  const form = AntdForm.useFormInstance()

  const fundingTypeId = AntdForm.useWatch('fundingTypeId', form)
  const instanceTypeId = AntdForm.useWatch('instanceTypeId', form)
  const overrideEnabled = AntdForm.useWatch('overrideInvoicingRate', form)
  const selectedCardId = AntdForm.useWatch('invoicingRateCardId', form)

  const { rateCard: autoRateCard } = useVisitRateCard(
    residentId,
    fundingTypeId,
    atDate,
    instanceTypeId
  )

  const { data: allRateCards = [] } = useGetInvoicingRateCards()

  if (!residentId || !fundingTypeId) return null

  if (!autoRateCard) {
    return (
      <FormItem
        label={<Text> </Text>}
        colon={false}
        style={{ marginBottom: 0 }}
      >
        <ToggleRow>
          <Switch size="small" disabled />
          <span style={{ color: '#999' }}>
            Override invoicing rate (no rate card configured)
          </span>
        </ToggleRow>
      </FormItem>
    )
  }

  const presetOptions = allRateCards
    .filter((c) => !c.locked && !c.deleted)
    .map((c) => {
      const { rate } = resolveEffectiveRate(c, instanceTypeId)
      return {
        label: `${c.name} (${c.currency}${parseFloat(rate)}/${c.unit.toLowerCase()})`,
        value: c._id
      }
    })

  const isCustom = selectedCardId === CUSTOM_VALUE

  const handleToggle = (on: boolean) => {
    if (on) {
      // initialValue on the FormItem is ignored because the field already exists in the
      // form store (set as undefined during form initialisation), so pre-select explicitly.
      // Mark as touched so overrideWasTouched detects this as a user-driven change.
      form.setFields([
        { name: 'invoicingRateCardId', value: autoRateCard._id, touched: true }
      ])
    } else {
      form.setFields([
        { name: 'invoicingRateCardId', value: undefined, touched: true },
        { name: 'customInvoicingRate', value: undefined, touched: true }
      ])
    }
  }

  return (
    <>
      {/* Toggle row — label column left blank to align with other fields */}
      <FormItem
        label={<Text> </Text>}
        colon={false}
        style={{ marginBottom: 0 }}
      >
        <ToggleRow>
          <AntdForm.Item
            name="overrideInvoicingRate"
            valuePropName="checked"
            noStyle
          >
            <Switch onChange={handleToggle} size="small" />
          </AntdForm.Item>
          <Text>Override invoicing rate</Text>
        </ToggleRow>
      </FormItem>

      {overrideEnabled && (
        <FormItem
          label={<Text>Invoicing rate</Text>}
          name="invoicingRateCardId"
          rules={[{ required: true, message: 'Please select a rate' }]}
          initialValue={autoRateCard._id}
        >
          {isCustom ? (
            <RateRow>
              <AntdForm.Item
                name="customInvoicingRate"
                noStyle
                rules={[
                  { required: true, message: 'Amount required' },
                  { type: 'number', min: 0.01, message: 'Must be > 0' }
                ]}
              >
                <InputNumber
                  min={0.01}
                  step={0.5}
                  style={{ width: 120 }}
                  formatter={(v) =>
                    !v && v !== 0
                      ? ''
                      : Number.isInteger(Number(v))
                        ? String(Number(v))
                        : String(v)
                  }
                  parser={(v) => (v ? parseFloat(v) : 0) as unknown as 0.01}
                />
              </AntdForm.Item>
              <Text>GBP / hour</Text>
              <ActionLink
                onClick={() => {
                  form.setFields([
                    {
                      name: 'invoicingRateCardId',
                      value: undefined,
                      touched: true
                    },
                    {
                      name: 'customInvoicingRate',
                      value: undefined,
                      touched: true
                    }
                  ])
                }}
              >
                Select from preset rates
              </ActionLink>
            </RateRow>
          ) : (
            <RateRow>
              <Select
                style={{ flex: 1 }}
                options={presetOptions}
                placeholder="Select rate"
                onChange={(val) =>
                  form.setFields([
                    { name: 'invoicingRateCardId', value: val, touched: true }
                  ])
                }
                value={selectedCardId}
              />
              <ActionLink
                onClick={() =>
                  form.setFields([
                    {
                      name: 'invoicingRateCardId',
                      value: CUSTOM_VALUE,
                      touched: true
                    }
                  ])
                }
              >
                Add a custom rate
              </ActionLink>
            </RateRow>
          )}
        </FormItem>
      )}
    </>
  )
}

const ToggleRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`

const RateRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
`

const ActionLink = styled.span`
  color: #1677ff;
  cursor: pointer;
  white-space: nowrap;
  font-size: inherit;

  &:hover {
    text-decoration: underline;
  }
`
